import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { OrgConfig } from '../types';
import { useAuth } from './AuthContext';
import api from '../utils/api';

interface OrgConfigContextType {
  config: OrgConfig | null;
  loading: boolean;
  refresh: () => Promise<void>;
  // Convenience permission helpers
  userCanEdit: boolean;
  userCanAdd: boolean;
  userCanRetire: boolean;
  userCanArchive: boolean;
  userCanChangeClassification: boolean;
}

const OrgConfigContext = createContext<OrgConfigContextType>(null!);

export function OrgConfigProvider({ children }: { children: React.ReactNode }) {
  const { user, token } = useAuth();
  const [config, setConfig] = useState<OrgConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const isManager = user?.role === 'admin' || user?.role === 'quartermaster';

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const data = await api.get<OrgConfig>('/org/config');
      setConfig(data);
    } catch (e) {
      console.error('Failed to load org config:', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      refresh();
    } else {
      setConfig(null);
      setLoading(false);
    }
  }, [token, refresh]);

  // Managers always have full permissions; standard users check org config
  const org = config?.organization;
  const userCanEdit = isManager || (org?.user_can_edit_own_gear ?? true);
  const userCanAdd = isManager || (org?.user_can_add_gear ?? true);
  const userCanRetire = isManager || (org?.user_can_retire_own_gear ?? true);
  const userCanArchive = isManager || (org?.user_can_archive_own_gear ?? true);
  const userCanChangeClassification = isManager || (org?.user_can_change_classification ?? true);

  return (
    <OrgConfigContext.Provider value={{
      config, loading, refresh,
      userCanEdit, userCanAdd, userCanRetire, userCanArchive, userCanChangeClassification,
    }}>
      {children}
    </OrgConfigContext.Provider>
  );
}

export function useOrgConfig() {
  return useContext(OrgConfigContext);
}
